/** @format */
// Migrated from AngularJS: educationStructurePreview.dir.js
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import * as _ from 'lodash-es';

export interface EducationModelSide {
    [key: string]: { size: 'large' | 'small' };
}

export interface EducationModel {
    left: EducationModelSide;
    right: EducationModelSide;
}

export interface ConnectionModel {
    [startNodeId: string]: { [endNodeId: string]: 'active' | 'inactive' };
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'education-structure-preview',
    templateUrl: './education-structure-preview.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class EducationStructurePreviewComponent implements OnChanges, AfterViewInit {
    @Input() educationModel: EducationModel;
    @Input() connectionModel: ConnectionModel;

    @ViewChild('leftArrowsCanvas') leftArrowsCanvasRef: ElementRef<HTMLCanvasElement>;
    @ViewChild('middleArrowsCanvas') middleArrowsCanvasRef: ElementRef<HTMLCanvasElement>;

    private startsDrawn: { [key: string]: string } = {};
    private endingsDrawn: { [key: string]: string } = {};
    private leftArrowsFromY: number[] = [];
    private viewInitialized = false;

    private readonly largeEducationSize = 52;
    private readonly smallEducationSize = 24;
    private readonly largeEducationDivider = 10;
    private readonly smallEducationDivider = 4;
    private readonly horizontalLinePartLength = 25;
    private readonly activeTriangleFactor = 7;
    private readonly inactiveTriangleFactor = 4;
    private readonly activeArrowColor = '#1076db';
    private readonly inactiveArrowColor = 'rgba(72,72,72)';

    ngAfterViewInit(): void {
        this.viewInitialized = true;
        this.updateArrows();
        this.updateArrows();
    }

    ngOnChanges(_changes: SimpleChanges): void {
        if (this.viewInitialized) {
            this.updateArrows();
            this.updateArrows();
        }
    }

    isStartActive(nodeId: string): boolean {
        const active1 = nodeId in this.startsDrawn && this.startsDrawn[nodeId] === 'active';
        const active2 = typeof this.connectionModel[nodeId] !== 'undefined' && Object.keys(this.connectionModel[nodeId]).length === 0;
        return active1 || active2;
    }

    isEndingActive(nodeId: string): boolean {
        return nodeId in this.endingsDrawn && this.endingsDrawn[nodeId] === 'active';
    }

    private updateArrows(): void {
        this.drawMiddleArrows();
        this.drawLeftArrows();
    }

    private drawLeftArrows(): void {
        const canvas = this.leftArrowsCanvasRef?.nativeElement;
        if (!canvas || !this.educationModel) {
            return;
        }

        const canvasSizeX = canvas.width;
        let smallestArrowStart = -1;
        let largestArrowStart = -1;

        if (typeof this.educationModel.left !== 'undefined' && Object.keys(this.educationModel.left).length !== 0 && canvas.getContext) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (this.leftArrowsFromY.length > 0) {
                const distinctValues = _.uniq(this.leftArrowsFromY);
                distinctValues.forEach(val => {
                    const endingY = parseInt(String(val), 10);
                    if (smallestArrowStart < 0 || endingY < smallestArrowStart) {
                        smallestArrowStart = endingY;
                    }
                    if (largestArrowStart < 0 || endingY > largestArrowStart) {
                        largestArrowStart = endingY;
                    }
                });

                let leftArrowStart: number;
                if (smallestArrowStart !== largestArrowStart) {
                    leftArrowStart = Math.round((smallestArrowStart + largestArrowStart) / 2);
                } else {
                    leftArrowStart = smallestArrowStart;
                }

                distinctValues.forEach(val => {
                    const endingY = parseInt(String(val), 10);
                    if (canvas.height < endingY) {
                        canvas.height = endingY + 10;
                    }

                    ctx.beginPath();
                    ctx.moveTo(0, leftArrowStart);
                    ctx.lineTo(this.horizontalLinePartLength - this.activeTriangleFactor, leftArrowStart);
                    ctx.lineTo(canvasSizeX - this.horizontalLinePartLength, endingY);
                    ctx.lineTo(canvasSizeX - this.activeTriangleFactor, endingY);
                    ctx.strokeStyle = this.activeArrowColor;
                    ctx.lineWidth = 2;
                    ctx.setLineDash([]);
                    ctx.stroke();
                    ctx.closePath();

                    ctx.beginPath();
                    ctx.moveTo(canvasSizeX, endingY);
                    ctx.lineTo(canvasSizeX - this.activeTriangleFactor * 1.5, endingY + this.activeTriangleFactor);
                    ctx.lineTo(canvasSizeX - this.activeTriangleFactor * 1.5, endingY - this.activeTriangleFactor);
                    ctx.fillStyle = this.activeArrowColor;
                    ctx.fill();
                    ctx.closePath();
                });
            }
        }
    }

    private getStartingY(startingConnectionNodeId: string): number {
        const startingConnectionNodeSize = this.educationModel.left[startingConnectionNodeId].size;
        let startingY = 0;

        const startNodeId = parseInt(startingConnectionNodeId, 10);
        for (let i = 1; i < startNodeId; i += 1) {
            switch (this.educationModel.left[i].size) {
                case 'large':
                    startingY += this.largeEducationSize;
                    if (i !== 1) {
                        startingY += this.largeEducationDivider;
                    }
                    break;
                case 'small':
                    startingY += this.smallEducationSize;
                    if (i !== 1) {
                        startingY += this.smallEducationDivider;
                    }
                    break;
                default:
                    break;
            }
        }

        switch (startingConnectionNodeSize) {
            case 'large':
                startingY += Math.floor(this.largeEducationSize / 2);
                if (startNodeId !== 1) {
                    startingY += this.largeEducationDivider;
                }
                break;
            case 'small':
                startingY += Math.floor(this.smallEducationSize / 2);
                if (startNodeId !== 1) {
                    startingY += this.smallEducationDivider;
                }
                break;
            default:
                break;
        }
        return startingY;
    }

    private drawMiddleArrows(): void {
        const canvas = this.middleArrowsCanvasRef?.nativeElement;
        if (!canvas || !this.educationModel || !this.connectionModel) {
            return;
        }

        const canvasSizeX = canvas.width;

        if (typeof this.educationModel.left !== 'undefined' && Object.keys(this.educationModel.left).length !== 0 && canvas.getContext) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            this.startsDrawn = {};
            this.endingsDrawn = {};
            this.leftArrowsFromY = [];

            Object.keys(this.connectionModel).forEach(startingConnectionNodeId => {
                if (Object.keys(this.connectionModel[startingConnectionNodeId]).length > 0) {
                    if (typeof this.educationModel.right !== 'undefined' && Object.keys(this.educationModel.right).length !== 0) {
                        Object.keys(this.connectionModel[startingConnectionNodeId]).forEach(endingConnectionNodeId => {
                            const endNodeId = parseInt(endingConnectionNodeId, 10);
                            const endingConnectionNodeSize = this.educationModel.right[endingConnectionNodeId].size;
                            const connectionStatus = this.connectionModel[startingConnectionNodeId][endingConnectionNodeId];

                            const startingY = this.getStartingY(startingConnectionNodeId);

                            let endingY = 0;
                            for (let j = 1; j < endNodeId; j += 1) {
                                switch (this.educationModel.right[j].size) {
                                    case 'large':
                                        endingY += this.largeEducationSize;
                                        if (j !== 1) {
                                            endingY += this.largeEducationDivider;
                                        }
                                        break;
                                    case 'small':
                                        endingY += this.smallEducationSize;
                                        if (j !== 1) {
                                            endingY += this.smallEducationDivider;
                                        }
                                        break;
                                    default:
                                        break;
                                }
                            }

                            switch (endingConnectionNodeSize) {
                                case 'large':
                                    endingY += Math.floor(this.largeEducationSize / 2);
                                    if (endNodeId !== 1) {
                                        endingY += this.largeEducationDivider;
                                    }
                                    break;
                                case 'small':
                                    endingY += Math.floor(this.smallEducationSize / 2);
                                    if (endNodeId !== 1) {
                                        endingY += this.smallEducationDivider;
                                    }
                                    break;
                                default:
                                    break;
                            }

                            ctx.beginPath();
                            if (!(startingConnectionNodeId in this.startsDrawn) || connectionStatus === 'active') {
                                ctx.moveTo(0, startingY);
                                ctx.lineTo(this.horizontalLinePartLength, startingY);
                                this.startsDrawn[startingConnectionNodeId] = connectionStatus;
                                if (connectionStatus === 'active') {
                                    this.leftArrowsFromY.push(startingY);
                                }
                                if (canvas.height < startingY) {
                                    canvas.height = startingY + 10;
                                }
                            } else {
                                ctx.moveTo(this.horizontalLinePartLength, startingY);
                            }

                            ctx.lineTo(canvasSizeX - this.horizontalLinePartLength, endingY);

                            if (!(endingConnectionNodeId in this.endingsDrawn) || connectionStatus === 'active') {
                                ctx.lineTo(canvasSizeX - this.inactiveTriangleFactor * 1.5, endingY);
                                if (canvas.height < endingY) {
                                    canvas.height = endingY + 10;
                                }
                            }

                            switch (connectionStatus) {
                                case 'active':
                                    ctx.strokeStyle = this.activeArrowColor;
                                    ctx.lineWidth = 2;
                                    ctx.setLineDash([]);
                                    ctx.stroke();
                                    ctx.closePath();

                                    ctx.beginPath();
                                    ctx.moveTo(canvasSizeX, endingY);
                                    ctx.lineTo(canvasSizeX - this.activeTriangleFactor * 1.5, endingY + this.activeTriangleFactor);
                                    ctx.lineTo(canvasSizeX - this.activeTriangleFactor * 1.5, endingY - this.activeTriangleFactor);
                                    ctx.fillStyle = this.activeArrowColor;
                                    ctx.fill();
                                    ctx.closePath();
                                    this.endingsDrawn[endingConnectionNodeId] = connectionStatus;
                                    break;
                                case 'inactive':
                                    ctx.strokeStyle = this.inactiveArrowColor;
                                    ctx.lineWidth = 1;
                                    ctx.setLineDash([5, 5]);
                                    ctx.stroke();
                                    ctx.closePath();

                                    if (!(endingConnectionNodeId in this.endingsDrawn)) {
                                        ctx.beginPath();
                                        ctx.moveTo(canvasSizeX, endingY);
                                        ctx.lineTo(canvasSizeX - this.inactiveTriangleFactor * 1.5, endingY + this.inactiveTriangleFactor);
                                        ctx.lineTo(canvasSizeX - this.inactiveTriangleFactor * 1.5, endingY - this.inactiveTriangleFactor);
                                        ctx.fillStyle = this.inactiveArrowColor;
                                        ctx.fill();
                                        ctx.closePath();
                                        this.endingsDrawn[endingConnectionNodeId] = connectionStatus;
                                    }
                                    break;
                                default:
                                    ctx.strokeStyle = '#ff0000';
                                    ctx.stroke();
                                    ctx.closePath();
                                    break;
                            }
                        });
                    }
                } else {
                    this.leftArrowsFromY.push(this.getStartingY(startingConnectionNodeId));
                }
            });
        }
    }
}
